import { useState, useEffect, useContext } from "react";
import isEqual from "lodash/isEqual";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Button, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { timeDiff, scrollToTop, convertToUnixTimeStamp } from "../../common/utils/functions";
import { useHistory } from "react-router-dom";
import { AccountContext } from "../../contexts/AccountContext";
import { InfoModal } from "./components/InfoModal";
import { EditTimeModal } from "./components/EditTimeModal";
import { requestsApi, printStripApi, smsApi } from "../../api/api";
import { CommentModal } from "./components/CommentModal";
import { ActiveTable, UpcomingTable, EarlierTable, ErasedTable, MapLayers } from "./components/AtsTables";
import { ActiveTable as ActiveCards, UpcomingTable as UpcomingCards, EarlierTable as EarlierCards, ErasedTable as ErasedCards } from "./components/AtsRequestCards";
import { AtsMap } from "./components/AtsMap";
import { ActiveToolbar } from "./components/ActiveToolbar";
import { UpcomingToolbar } from "./components/UpcomingToolbar";
import { EarlierToolbar } from "./components/EarlierToolbar";
import { ErasedToolbar } from "./components/ErasedToolbar";
import { useTranslation } from "react-i18next";
import { generatePrintStrip } from "../../common/utils/printStrip";
import ConfirmDialog from "../../common/components/ConfirmDialog";
import { REQUEST_STATUS } from "../../constants/constants";
import { useActive } from "../../common/hooks/useActive";
import { useUpcoming } from "../../common/hooks/useUpcoming";
import { useEarlier } from "../../common/hooks/useEarlier";
import { useErased } from "../../common/hooks/useErased";
import { useStandard } from "../../common/hooks/useStandard";
import { useAtsMapData } from "../../common/hooks/useAtsMapData";
import { usePositionStatus } from "../../common/hooks/usePositionStatus";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const Ats = ({ setSelected, handleOpenSnackbar }) => {
  const { t } = useTranslation();
  const { user } = useContext(AccountContext);
  const mqttPositions = usePositionStatus();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const RequestActiveTable = isMobile ? ActiveCards : ActiveTable;
  const RequestUpcomingTable = isMobile ? UpcomingCards : UpcomingTable;
  const RequestEarlierTable = isMobile ? EarlierCards : EarlierTable;
  const RequestErasedTable = isMobile ? ErasedCards : ErasedTable;

  const [selectedRequest, setSelectedRequest] = useState();

  const [highlightActive, setHighlightActive] = useState(null);
  const [highlightUpcoming, setHighlightUpcoming] = useState(null);
  const [highlightEarlier, setHighlightEarlier] = useState(null);
  const [highlightErased, setHighlightErased] = useState(null);
  const [highlightStandardRequest, setHighlightStandardRequest] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [previewZone, setPreviewZone] = useState();
  const [zones, setZones] = useState();
  const [toggleZones, setToggleZones] = useState(true);
  const [, setSelectedUser] = useState();

  const [showComments, setShowComments] = useState(false);
  const [showRequestInfo, setShowRequestInfo] = useState(false);
  const [showRequestEdit, setShowRequestEdit] = useState(false);

  const [tab, setTab] = useState(0);

  const [searchActive, setSearchActive] = useState("");
  const [searchUpcoming, setSearchUpcoming] = useState("");
  const [searchEarlier, setSearchEarlier] = useState("");
  const [searchErased, setSearchErased] = useState("");

  const handleCloseComments = () => setShowComments(false);
  const handleShowComments = () => setShowComments(true);

  const handleCloseRequestInfo = () => setShowRequestInfo(false);
  const handleShowRequestInfo = () => setShowRequestInfo(true);

  const handleCloseRequestEdit = () => setShowRequestEdit(false);
  const handleShowRequestEdit = () => setShowRequestEdit(true);

  let history = useHistory();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: active = [], refetch: refetchActive, isFetching: isFetchingActive } = useActive(user?.atsIcaoLocationIndicator, searchActive);

  const { data: upcoming = [], refetch: refetchUpcoming, isFetching: isFetchingUpcoming } = useUpcoming(user?.atsIcaoLocationIndicator, searchUpcoming);

  const { data: earlier = [], refetch: refetchEarlier, isFetching: isFetchingEarlier } = useEarlier(user?.atsIcaoLocationIndicator, searchEarlier);

  const { data: erased = [], refetch: refetchErased, isFetching: isFetchingErased } = useErased(user?.atsIcaoLocationIndicator, searchErased);

  const { data: standard = [], isFetching: isFetchingStandard } = useStandard(user?.atsId);

  const {
    data: {
      ctrAirspaces = [],
      restrictedAreasAirspaces = [],
      bufferZonesAirspaces = [],
      futureObjectsAirspaces = [],
      trafficInformationZonesAirspaces = [],
      airTrafficZonesAirspaces = [],
      restrictedAreasTemporaryAirspaces = [],
      uasZonesAirspaces = [],
      drones = [],
    } = {},
  } = useAtsMapData(user);

  const isBusy = isLoading || isFetchingActive || isFetchingUpcoming || isFetchingEarlier || isFetchingErased || isFetchingStandard;

  const refreshActive = () => {
    return refetchActive();
  };

  const refreshUpcoming = () => {
    return refetchUpcoming();
  };

  const refreshEarlier = () => {
    return refetchEarlier();
  };

  const refreshErased = () => {
    return refetchErased();
  };

  const refreshAtsLists = () => {
    return Promise.all([refreshActive(), refreshUpcoming(), refreshEarlier(), refreshErased()]);
  };

  const searchActiveRequests = (searchValue) => {
    if (!user?.atsIcaoLocationIndicator) return Promise.resolve([]);
    return requestsApi.getByAts(user.atsIcaoLocationIndicator, REQUEST_STATUS.ACTIVE, searchValue.trim()).then((r) => r.data);
  };

  const searchUpcomingRequests = (searchValue) => {
    if (!user?.atsIcaoLocationIndicator) return Promise.resolve([]);
    return requestsApi.getByAts(user.atsIcaoLocationIndicator, REQUEST_STATUS.UPCOMING, searchValue.trim()).then((r) => r.data);
  };

  const searchEarlierRequests = (searchValue) => {
    if (!user?.atsIcaoLocationIndicator) return Promise.resolve([]);
    return requestsApi.getByAts(user.atsIcaoLocationIndicator, REQUEST_STATUS.EARLIER, searchValue.trim()).then((r) => r.data);
  };

  const searchErasedRequests = (searchValue) => {
    if (!user?.atsIcaoLocationIndicator) return Promise.resolve([]);
    return requestsApi.getByAts(user.atsIcaoLocationIndicator, REQUEST_STATUS.ERASED, searchValue.trim()).then((r) => r.data);
  };

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
  };

  useEffect(() => {
    if (!user) history.push("/");
    else if (user.changePassword) history.push("/change-password");
    else if (!user.roles.includes("Ats")) history.push("/unauthorized");
    else if (user.atsName === null) {
      history.push("/noAts");
    }
    scrollToTop();

    return () => {
      // cleanup
      setSearchActive("");
      setSearchUpcoming("");
      setSearchEarlier("");
      setSearchErased("");
    };
  }, [history, user]);

  const activateRequest = (request) => {
    setSearchUpcoming("");
    setIsLoading(true);
    requestsApi
      .activateRequest(request.id, "/activatedDateByAts", "replace", convertToUnixTimeStamp(), user?.atsIcaoLocationIndicator)
      .then(() => {
        Promise.all([refetchActive(), refetchUpcoming()])
          .then(() => {
            setIsLoading(false);
          })
          .catch((err) => {
            console.log(err);
            setIsLoading(false);
          });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const endRequest = (request) => {
    setSearchActive("");
    setIsLoading(true);
    requestsApi
      .endRequest(request.id, "/endedDateByAts", "replace", convertToUnixTimeStamp())
      .then(() => {
        requestsApi
          .patchRequest(request.id, "/flightTime", "replace", timeDiff(request.activatedDateByAts))
          .then(() => {
            Promise.all([refetchActive(), refetchEarlier()])
              .then(() => {
                setHighlightActive();
                setIsLoading(false);
              })
              .catch((err) => {
                console.log(err);

                setIsLoading(false);
              });
          })
          .catch((err) => {
            console.log(err);
            setIsLoading(false);
          });
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onErase = (request) => {
    setSearchUpcoming("");
    requestsApi.patchRequest(request.id, "/erased", "replace", true).then(() => {
      Promise.all([refetchUpcoming(), refetchErased()])
        .then(() => {
          setHighlightUpcoming();
        })
        .catch((err) => {
          console.log(err);
        });
    });
  };

  const onPostCopied = (request) => {
    setIsLoading(true);
    requestsApi
      .postCopiedRequest(request, convertToUnixTimeStamp())
      .then((res) => {
        if (res.status.toString().startsWith("20")) {
          // delay to allow the copied request to be created
          setTimeout(() => {
            refetchUpcoming()
              .then(() => {
                setIsLoading(false);
              })
              .catch((err) => {
                setIsLoading(false);
              });
          }, 200);
        } else {
          setIsLoading(false);
          console.log("response: ", res);
          console.log("response: ", res.data);

          handleOpenSnackbar("error", t("copy_failed"));
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log("error response: ", err);
        console.log("error response: ", err.data);
        console.log("error response: ", err.response);
        console.log("error response: ", err.response.data);
        console.log("error response: ", err.message);

        handleOpenSnackbar("error", t("copy_failed"));
      });
  };

  // const onHighlightActive = (e, request) => {
  //   if (!highlightActive) setHighlightActive(request);
  //   else {
  //     const current = [request.lat, request.lng];
  //     const previous = [highlightActive.lat, highlightActive.lng];

  //     if (isEqual(current, previous)) {
  //       setHighlightActive();
  //     } else {
  //       setHighlightActive(request);
  //     }
  //   }
  // };

  const onHighlightActive = (e, request) => {
    if (!highlightActive) {
      setHighlightActive(request);
    }

    if (isEqual(request, highlightActive)) {
      setHighlightActive();
    } else {
      setHighlightActive(request);
    }
    // }
  };

  const onHighlightUpcoming = (e, request) => {
    if (!highlightUpcoming) {
      setHighlightUpcoming(request);
    } else {
      const current = [request.lat, request.lng];
      const previous = [highlightUpcoming.lat, highlightUpcoming.lng];

      if (isEqual(current, previous)) {
        setHighlightUpcoming();
      } else {
        setHighlightUpcoming(request);
      }
    }
  };

  const onHighlightEarlier = (e, request) => {
    if (!highlightEarlier) {
      setHighlightEarlier(request);
    } else {
      const current = [request.lat, request.lng];
      const previous = [highlightEarlier.lat, highlightEarlier.lng];

      if (isEqual(current, previous)) {
        setHighlightEarlier();
      } else {
        setHighlightEarlier(request);
      }
    }
  };

  const onHighlightErased = (e, request) => {
    if (!highlightErased) {
      setHighlightErased(request);
    } else {
      const current = [request.lat, request.lng];
      const previous = [highlightErased.lat, highlightErased.lng];

      if (isEqual(current, previous)) {
        setHighlightErased();
      } else {
        setHighlightErased(request);
      }
    }
  };

  const onHighlightStandardRequest = (request) => {
    if (isEqual(request.area, previewZone)) {
      setPreviewZone();
      setHighlightStandardRequest();
    } else {
      setPreviewZone(request.area);
      setHighlightStandardRequest(request);
    }
  };

  const activateStandardRequest = (request) => {
    if (zones) {
      setZones([...zones, request]);
    } else setZones([request]);
  };

  const endStandardRequest = (request) => {
    const updatedList = zones.filter((zone) => zone.id !== request.id);
    setZones(updatedList);
  };

  const printAida = async (request) => {
    await printStripApi.post(generatePrintStrip(request));
  };

  const handleCancel = () => {
    smsApi
      .sendOne(selectedRequest.phoneNumber, selectedRequest.displayName, user.atsPhoneNumber)
      .then((res) => {})
      .catch((err) => {});
  };

  return (
    <div className="standard-container">
      <div className="item">
        <ConfirmDialog
          open={confirmOpen}
          onClose={setConfirmOpen}
          onConfirm={() => {
            setConfirmOpen(false);
            handleCancel();
          }}
          title={t("confirm_title")}
          message={t("confirm_abort")}
        />
        <CommentModal show={showComments} handleClose={handleCloseComments} selectedRequest={selectedRequest} user={user} refreshAtsLists={refreshAtsLists} setIsLoading={setIsLoading} />
        <InfoModal atsId={user.atsId} atsBetaFeatures={user.atsBetaFeatures} show={showRequestInfo} handleClose={handleCloseRequestInfo} selectedRequest={selectedRequest} />
        <EditTimeModal
          earlier={earlier}
          show={showRequestEdit}
          handleClose={handleCloseRequestEdit}
          selectedRequest={selectedRequest}
          handleOpenSnackbar={handleOpenSnackbar}
          setIsLoading={setIsLoading}
          refreshEarlier={refreshEarlier}
        />
        <div className="map-container">
          <AtsMap
            active={active}
            user={user}
            zones={zones}
            previewZone={previewZone}
            toggleZones={toggleZones}
            highlightActive={highlightActive}
            highlightUpcoming={highlightUpcoming}
            highlightEarlier={highlightEarlier}
            highlightErased={highlightErased}
            ctrAirspaces={ctrAirspaces}
            restrictedAreasAirspaces={restrictedAreasAirspaces}
            bufferZonesAirspaces={bufferZonesAirspaces}
            futureObjectsAirspaces={futureObjectsAirspaces}
            trafficInformationZonesAirspaces={trafficInformationZonesAirspaces}
            airTrafficZonesAirspaces={airTrafficZonesAirspaces}
            restrictedAreasTemporaryAirspaces={restrictedAreasTemporaryAirspaces}
            uasZonesAirspaces={uasZonesAirspaces}
            drones={drones}
          />
          <div style={{ marginTop: 6 }}></div>
          <Button
            variant="contained"
            color="info"
            sx={{ textTransform: "none" }}
            size="small"
            onClick={() => {
              toggleZones ? setToggleZones(false) : setToggleZones(true);
            }}
          >
            {t("show_hide_restricted_areas")}
          </Button>
        </div>
      </div>
      <div className="item">
        <Tabs
          value={tab}
          onChange={(e, newValue) => {
            setTab(newValue);
            setHighlightActive();
            setHighlightUpcoming();
            setHighlightEarlier();
            setHighlightErased();
            setPreviewZone();
          }}
          aria-label="tabs"
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label={t("active")} />
          <Tab label={t("upcoming")} />
          <Tab label={t("earlier")} />
          <Tab label={t("removed")} />
          <Tab label={t("maplayers")} />
        </Tabs>
        <TabPanel value={tab} index={0}>
          <ActiveToolbar
            active={active}
            search={searchActive}
            setSearch={setSearchActive}
            setIsLoading={setIsLoading}
            setHighlightActive={setHighlightActive}
            onSearch={searchActiveRequests}
            onRefresh={refreshActive}
            onClearSearch={refreshActive}
            isBusy={isBusy}
          />
          <RequestActiveTable
            active={active}
            onHighlightActive={onHighlightActive}
            highlightActive={highlightActive}
            endRequest={endRequest}
            setSelectedUser={setSelectedUser}
            handleShowComments={handleShowComments}
            handleShowRequestInfo={handleShowRequestInfo}
            selectedRequest={selectedRequest}
            setSelectedRequest={setSelectedRequest}
            setSelected={setSelected}
            history={history}
            user={user}
            printAida={printAida}
            handleOpenConfirm={handleOpenConfirm}
            mqttPositions={mqttPositions}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <UpcomingToolbar
            search={searchUpcoming}
            setSearch={setSearchUpcoming}
            setIsLoading={setIsLoading}
            setHighlightUpcoming={setHighlightUpcoming}
            onSearch={searchUpcomingRequests}
            onRefresh={refreshUpcoming}
            onClearSearch={refreshUpcoming}
            isBusy={isBusy}
          />
          <RequestUpcomingTable
            upcoming={upcoming}
            selectedRequest={selectedRequest}
            onHighlightUpcoming={onHighlightUpcoming}
            highlightUpcoming={highlightUpcoming}
            setHighlightUpcoming={setHighlightUpcoming}
            activateRequest={activateRequest}
            onErase={onErase}
            setSelectedUser={setSelectedUser}
            user={user}
            handleShowComments={handleShowComments}
            handleShowRequestInfo={handleShowRequestInfo}
            setSelectedRequest={setSelectedRequest}
            setSelected={setSelected}
            history={history}
            printAida={printAida}
            mqttPositions={mqttPositions}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <EarlierToolbar
            search={searchEarlier}
            setSearch={setSearchEarlier}
            setIsLoading={setIsLoading}
            setHighlightEarlier={setHighlightEarlier}
            onSearch={searchEarlierRequests}
            onRefresh={refreshEarlier}
            onClearSearch={refreshEarlier}
            isBusy={isBusy}
          />
          <RequestEarlierTable
            selectedRequest={selectedRequest}
            earlier={earlier}
            onHighlightEarlier={onHighlightEarlier}
            highlightEarlier={highlightEarlier}
            onPostCopied={onPostCopied}
            setSelectedUser={setSelectedUser}
            user={user}
            handleShowComments={handleShowComments}
            handleShowRequestInfo={handleShowRequestInfo}
            handleShowRequestEdit={handleShowRequestEdit}
            setSelectedRequest={setSelectedRequest}
            setSelected={setSelected}
            history={history}
            printAida={printAida}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={tab} index={3}>
          <ErasedToolbar
            search={searchErased}
            setSearch={setSearchErased}
            setIsLoading={setIsLoading}
            setHighlightErased={setHighlightErased}
            onSearch={searchErasedRequests}
            onRefresh={refreshErased}
            onClearSearch={refreshErased}
            isBusy={isBusy}
          />
          <RequestErasedTable
            selectedRequest={selectedRequest}
            erased={erased}
            onHighlightErased={onHighlightErased}
            highlightErased={highlightErased}
            onPostCopied={onPostCopied}
            setSelectedUser={setSelectedUser}
            handleShowComments={handleShowComments}
            handleShowRequestInfo={handleShowRequestInfo}
            setSelectedRequest={setSelectedRequest}
            setSelected={setSelected}
            history={history}
            printAida={printAida}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={tab} index={4}>
          <MapLayers
            standard={standard}
            setSelectedUser={setSelectedUser}
            zones={zones}
            onHighlightStandardRequest={onHighlightStandardRequest}
            highlightStandardRequest={highlightStandardRequest}
            activateStandardRequest={activateStandardRequest}
            endStandardRequest={endStandardRequest}
          />
        </TabPanel>
      </div>
    </div>
  );
};

export default Ats;
